/** @format */
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
    DOCUMENT,
} from '@angular/core';
import * as _ from 'lodash-es';
import { Observable, Subscription } from 'rxjs';
import { AttainmentTreeService } from './attainment-tree.service';
import {
    CollapsedByGroupingModuleId,
    CourseUnitsById,
    IndexedAttainments,
    ModulesById,
    ModuleSelectionWithOrdering,
    PlanWithOrdering,
    SelectionItem,
    StudySelection,
} from './AttainmentTreeTypes';

@Component({
    selector: 'sis-editable-attainment-tree-study-selections',
    templateUrl: './editable-attainment-tree-study-selections.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class EditableAttainmentTreeStudySelectionsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() moduleSelection: ModuleSelectionWithOrdering;
    @Input() moduleContent: PlanWithOrdering;
    @Input() indexedAttainments: IndexedAttainments;
    @Input() courseUnitsById: CourseUnitsById;
    @Input() modulesById: ModulesById;
    @Input() selectedLocale: string;
    @Input() collapsedByGroupingModuleId: CollapsedByGroupingModuleId;
    // this emits events upwards in component tree - see explanation in EditableAttainmentTreeRootComponent
    @Output() childrenUpdated = new EventEmitter<void>();
    // this emits events downwards in component tree - see explanation in EditableAttainmentTreeRootComponent
    @Input() groupingModuleVisibilityToggled: Observable<void>;
    studySelectionItems: SelectionItem[] = [];
    private subscription = Subscription.EMPTY;

    constructor(
        private attainmentTreeService: AttainmentTreeService,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.moduleContent) {
            this.refreshItems();
        }
    }

    ngOnInit() {
        this.subscription = this.groupingModuleVisibilityToggled.subscribe(() => this.refreshItems());
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    onChildrenUpdated() {
        this.refreshItems();
        this.childrenUpdated.emit();
    }

    move(ordering: number, upwards: boolean) {
        this.attainmentTreeService.moveSelection(this.moduleSelection, this.moduleContent, ordering, upwards);
        this.refreshItems();
    }

    calculateItemId(index: number, item: SelectionItem): string {
        return item?.type === 'Attainment'
            ? `att-${item.attainment?.id}`
            : `gm-${(item?.studySelection as ModuleSelectionWithOrdering)?.moduleId}`;
    }

    // unfortunately exposed for testing
    refreshItems() {
        const orderedStudySelectionItems = this.attainmentTreeService.getChildrenVisibleUnderModule(
            this.moduleSelection,
            this.moduleContent,
        );
        this.attainmentTreeService.addAttainmentsToSelectionItems(
            orderedStudySelectionItems,
            this.indexedAttainments,
            this.modulesById,
            this.courseUnitsById,
        );
        this.studySelectionItems = orderedStudySelectionItems;
    }

    addHoverStyles(item: SelectionItem) {
        const hoveredElementId = this.calculateItemId(0, item);
        const siblings = document.getElementById(hoveredElementId).parentNode.childNodes;

        // change siblings' (the hovered element included) background-color
        _.forEach(siblings, (sibling: HTMLElement) => {
            if (sibling.id !== undefined) {
                sibling.style.backgroundColor = '#D6E9FC';

                // changed background-color applies also to siblings' children, this removes it
                const tagName = 'sis-editable-attainment-tree-study-selections';
                const childrenContainer = sibling.getElementsByTagName(tagName)[0] as HTMLElement;
                if (childrenContainer !== undefined) {
                    childrenContainer.style.backgroundColor = 'white';
                }
            }
        });
    }

    removeHoverStyles(item: SelectionItem) {
        const hoveredElementId = this.calculateItemId(0, item);
        const siblings = document.getElementById(hoveredElementId).parentNode.childNodes;

        _.forEach(siblings, (sibling: HTMLElement) => {
            if (sibling.id !== undefined) {
                sibling.style.backgroundColor = 'white';
            }
        });
    }

    getValidModuleSelection(selection: StudySelection): ModuleSelectionWithOrdering {
        if ('moduleId' in selection) {
            return selection as ModuleSelectionWithOrdering;
        }
    }
}
